import * as React from 'react';
import { Check, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';

export type CheckboxProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  /** Shown as a dash: some, but not all, of what this box governs is ticked. */
  indeterminate?: boolean;
};

/**
 * A native checkbox drawn to the brand instead of the browser's default. The
 * input stays real, so keyboard, forms and screen readers see a checkbox; it is
 * made invisible over a drawn box that reads its state through `peer`.
 */
export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { className, indeterminate = false, ...props },
  forwardedRef,
) {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  React.useImperativeHandle(forwardedRef, () => inputRef.current as HTMLInputElement);
  React.useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <span className={cn('relative inline-flex size-4 shrink-0 align-middle', className)}>
      <input
        ref={inputRef}
        type="checkbox"
        className="peer absolute inset-0 z-10 size-full cursor-pointer appearance-none rounded-[4px] border border-[hsl(var(--muted-foreground))]/45 bg-[hsl(var(--background))] shadow-[inset_0_1px_1px_rgba(0,0,0,0.04)] transition-colors hover:border-[hsl(var(--brand))]/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-40 checked:border-[hsl(var(--brand))] checked:bg-[hsl(var(--brand))] indeterminate:border-[hsl(var(--brand))] indeterminate:bg-[hsl(var(--brand))]"
        {...props}
      />
      <Check
        aria-hidden
        strokeWidth={3.5}
        className="pointer-events-none absolute inset-0 z-20 m-auto size-3 text-white opacity-0 peer-checked:opacity-100"
      />
      <Minus
        aria-hidden
        strokeWidth={3.5}
        className="pointer-events-none absolute inset-0 z-20 m-auto size-3 text-white opacity-0 peer-indeterminate:opacity-100"
      />
    </span>
  );
});
